import { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import { useChannel } from "ably/react";
import {
  Button,
  DialogModal,
  Divider,
  Grid,
  IconButton,
  Icons,
  PageHeader,
  Tabs,
  Text,
  useDevice,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import Dashboard from "../../layouts/Dashboard";
import MigratingTabView from "./components/MigratingTabView";
import ActivatedTabView from "./components/ActivatedTabView";
import {
  useActivePlansQuery,
  useClientInfoQuery,
  useGetMembersData,
} from "./api/migrationAssistantApi.hooks";
import AssignPlansTable from "./components/plans/AssignPlansTable";
import PageLoadingState from "./components/shared/PageLoadingState";
import ErrorState from "./components/shared/ErrorState";
import { AppContext } from "../../context/app";
import ActivatingScreen from "./components/shared/ActivatingScreen";
import { MIGRATION_ASSISTANT_ASYNC_ACTIVATION } from "../../context/features/features.utils";
import { useFeatureFlag } from "../../context/features";
import { defaultToastOptions } from "../Appointments/AppointmentsUtils";
import { getMembersReadyForActivation } from "./helpers";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import ReviewAndActivateModal from "./components/activate/ReviewAndActivateModal";
import { AblyMessageDataType } from "./types";

export default function MigrationAssistant() {
  return (
    <>
      <Helmet>
        <title>Migration Assistant</title>
      </Helmet>
      <Dashboard scrollableContent disableContentPadding>
        <MigrationAssistantContent />
      </Dashboard>
    </>
  );
}

export function MigrationAssistantContent() {
  const app = useContext(AppContext);
  const { trackEvent } = useAnalytics();
  const { isFeatureEnabled } = useFeatureFlag();
  const { isMobile } = useDevice();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [showAssignPlansPage, setShowAssignPlansPage] = useState(false);
  const [isStripeModalVisible, setIsStripeModalVisible] = useState(false);
  const [showActivatingScreen, setShowActivatingScreen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [totalMembersCount, setTotalMembersCount] = useState(0);

  // Remove non-async after async is fully implemented and working
  const enableAsyncActivation = isFeatureEnabled(
    MIGRATION_ASSISTANT_ASYNC_ACTIVATION,
  );

  trackEvent("Migration Assistant Viewed", {
    is_mobile: isMobile,
  });

  useChannel(`client.${app.client.uuid}.automated.migrations`, (message) => {
    // show activating screen until all members are activated
    const messageData: AblyMessageDataType = message?.data?.data;

    if (messageData?.migrating === false) {
      if (showActivatingScreen) {
        setShowActivatingScreen(false);
        toast.success("Members activated", defaultToastOptions);
        setTotalMembersCount(0);
        refetchMembersData();
      }
    } else {
      if (!showActivatingScreen) {
        setShowActivatingScreen(true);
      }
      if (!totalMembersCount) {
        setTotalMembersCount(messageData?.total);
      }
    }
  });

  const {
    data: membersData,
    isFetching: membersDataFetching,
    refetch: refetchMembersData,
    error: membersDataError,
  } = useGetMembersData();
  const { data: activePlansData, isFetching: activePlansDataFetching } =
    useActivePlansQuery();
  const { data: clientInfo, isFetching: clientInfoFetching } =
    useClientInfoQuery();

  const migratingMembers =
    (Array.isArray(membersData) &&
      membersData.filter((member) => !member.migrated)) ||
    [];

  const migratedMembers =
    (Array.isArray(membersData) &&
      membersData.filter((member) => member.migrated)) ||
    [];

  const membersReadyForActivation = getMembersReadyForActivation(membersData);
  const migratingMembersCount = migratingMembers?.length || 0;
  const activatedMembersCount = migratedMembers?.length || 0;

  const membersWithPlansCount =
    migratingMembers?.filter((member) => member.planUuid || !member.needPlan)
      .length || 0;

  const tabHeaders = [
    `Migrating (${migratingMembersCount})`,
    `Activated (${activatedMembersCount})`,
  ];
  const showMigratingTab = activeTab === 0;
  const showActivatedTab = activeTab === 1;

  const preventActivation = !clientInfo?.stripeUserId;

  const isDataFetching =
    membersDataFetching || activePlansDataFetching || clientInfoFetching;

  if (!isDataFetching && !membersData) {
    trackEvent("Migration Assistant Error", {
      error: "ma_no_members_data_api_error",
      api_error: JSON.stringify(membersDataError),
    });
  }

  return (
    <Grid container justifyContent="space-between">
      {showAssignPlansPage && activePlansData && (
        <Grid item marginX={10} xs={10}>
          <AssignPlansTable
            membersData={migratingMembers}
            activePlansData={activePlansData.data || []}
            setShowAssignPlansPage={setShowAssignPlansPage}
          />
        </Grid>
      )}

      {!showAssignPlansPage && (
        <>
          {isDataFetching && <PageLoadingState />}

          {!isDataFetching && !membersData && (
            <ErrorState refetch={refetchMembersData} />
          )}

          {!isDataFetching && membersData && (
            <>
              <Grid
                item
                marginX={10}
                md={7}
                xs={12}
                style={{ marginTop: "2rem" }}
              >
                <PageHeader title="Migration assistant">
                  {!showActivatingScreen && (
                    <PageHeader.ActionMenu>
                      <IconButton
                        icon={Icons.Help}
                        tooltip="Help"
                        onClick={() => {
                          window
                            .open(
                              "https://help.pushpress.com/en/?q=migration",
                              "_blank",
                            )
                            ?.focus();
                        }}
                      />
                    </PageHeader.ActionMenu>
                  )}
                </PageHeader>
              </Grid>

              {!showActivatingScreen && (
                <Grid item marginX={10} md={7} xs={12}>
                  <Tabs
                    tabHeaders={tabHeaders}
                    selectedIndex={activeTab}
                    changeTab={(index) => setActiveTab(index)}
                    onTabClick={(index) => setActiveTab(index)}
                    onTabFocus={(index) => setActiveTab(index)}
                    align="left"
                    hideWithCss
                  />
                </Grid>
              )}

              {showActivatingScreen && (
                <Grid
                  item
                  marginX={isMobile ? 5 : 25}
                  md={7}
                  xs={isMobile ? 12 : 5}
                  style={{ marginTop: "2rem" }}
                >
                  <ActivatingScreen
                    enableAsyncActivation={enableAsyncActivation}
                    totalCount={totalMembersCount}
                  />
                </Grid>
              )}

              {showMigratingTab && !showActivatingScreen && (
                <Grid item marginX={10} md={7} xs={12}>
                  <MigratingTabView
                    preventActivation={preventActivation}
                    setIsStripeModalVisible={setIsStripeModalVisible}
                    migratingMembersCount={migratingMembersCount}
                    membersWithPlansCount={membersWithPlansCount}
                    setShowAssignPlansPage={setShowAssignPlansPage}
                    membersReadyForActivationCount={
                      membersReadyForActivation.length
                    }
                    setIsActivateModalOpen={setIsActivateModalOpen}
                  />
                </Grid>
              )}

              {showActivatedTab && (
                <Grid item marginX={10} md={7} xs={12} marginY={3}>
                  <ActivatedTabView membersData={migratedMembers} />
                </Grid>
              )}
            </>
          )}
        </>
      )}
      <ReviewAndActivateModal
        membersData={membersReadyForActivation}
        plans={activePlansData?.data || []}
        isModalOpen={isActivateModalOpen}
        setIsModalOpen={setIsActivateModalOpen}
        setShowActivatingScreen={setShowActivatingScreen}
      />
      <DialogModal
        title="Unable to activate"
        visible={isStripeModalVisible}
        primaryBtnTitle="Close"
        primaryButtonOptions={{ fullWidth: true }}
        secondaryBtnTitle=""
        primaryHandler={() => setIsStripeModalVisible(false)}
        primaryType="primary"
      >
        <Divider />
        <Grid
          item
          marginY={2}
          container
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <Text variant="body-lg">Please</Text>
          </Grid>
          <Grid item>
            <Button
              href="/settings/bank"
              target="_blank"
              text="connect Stripe to your bank account"
              buttonType="text"
            />
          </Grid>
          <Grid item>
            <Text variant="body-lg">before activating members.</Text>
          </Grid>
        </Grid>
      </DialogModal>
    </Grid>
  );
}
